// OnboardingChat Component
// ========================
// Chat-based onboarding where Anna guides the user through creating their first
// learning journey via a conversational bubble UI.
//
// Steps: greeting → upload → timeframe → creating → done → /dashboard

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useAuth } from '../../contexts/AuthContext';
import { readFileContent } from '../../../utils/fileUtils';
import AnnaGlow from '../ui/AnnaGlow';
import { ChatBubble } from './ChatBubble';

// ── Types ──────────────────────────────────────────────────────────────────

type ChatStep = 'greeting' | 'welcome' | 'upload' | 'timeframe' | 'creating' | 'done';

type Timeframe = '1w' | '2w' | '1m';

const TIMEFRAME_DAYS: Record<Timeframe, number> = {
  '1w': 7,
  '2w': 14,
  '1m': 30,
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// ── Component ─────────────────────────────────────────────────────────────

interface OnboardingChatProps {
  /** 'first' = onboarding (default), 'additional' = create another journey */
  mode?: 'first' | 'additional';
}

const OnboardingChat: React.FC<OnboardingChatProps> = ({ mode = 'first' }) => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { createJourney } = useLearningJourneys();
  const { user, updateUser } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || t('defaultFirstName');

  // ── State ────────────────────────────────────────────────────────────────

  const [step, setStep] = useState<ChatStep>('greeting');
  const [textInput, setTextInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedContent, setUploadedContent] = useState('');
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdJourneyId, setCreatedJourneyId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ── Auto-advance: greeting → welcome (first) or upload (additional) ────

  useEffect(() => {
    if (step === 'greeting') {
      const next = mode === 'first' ? 'welcome' : 'upload';
      const timer = setTimeout(() => setStep(next), 800);
      return () => clearTimeout(timer);
    }
  }, [step, mode]);

  // ── Auto-advance: done → /dashboard ─────────────────────────────────────

  useEffect(() => {
    if (step === 'done') {
      // After journey creation, go to topic planner so Anna can build the study plan
      const target = mode === 'additional' && createdJourneyId
        ? `/library/journey/${createdJourneyId}`
        : '/dashboard';
      const timer = setTimeout(() => navigate(target), 1500);
      return () => clearTimeout(timer);
    }
  }, [step, navigate, mode, createdJourneyId]);

  // ── Scroll chat to bottom on step change ────────────────────────────────

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [step]);

  // ── File handling ────────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    setError('');

    if (file.size > MAX_FILE_SIZE) {
      setError(t('chat.errorFileTooLarge'));
      return;
    }

    try {
      const content = await readFileContent(file);
      setUploadedFile(file);
      setUploadedContent(content);
    } catch {
      setError(t('chat.errorFileRead'));
    }
  }, [t]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // ── Submit curriculum description / file ─────────────────────────────────

  const handleUploadSubmit = useCallback(() => {
    const hasContent = textInput.trim().length > 0 || uploadedContent.length > 0;
    if (!hasContent) {
      setError(t('chat.errorEmpty'));
      return;
    }
    setError('');
    setStep('timeframe');
  }, [textInput, uploadedContent, t]);

  // ── Timeframe selection → create journey ────────────────────────────────

  const handleTimeframeSelect = useCallback(async (tf: Timeframe) => {
    setStep('creating');
    setIsCreating(true);

    // Calculate exam date from timeframe
    const examDate = new Date();
    examDate.setDate(examDate.getDate() + TIMEFRAME_DAYS[tf]);

    // Determine subject: prefer uploaded filename, else first line of text input
    const subject = uploadedFile
      ? uploadedFile.name.replace(/\.[^/.]+$/, '') // strip extension
      : (textInput.split('\n')[0].slice(0, 80) || 'My study subject');

    // Persist pensum for later AI processing
    if (uploadedContent) {
      localStorage.setItem('pendingPensum', uploadedContent);
    }

    const result = await createJourney({ subject, examDate });
    setIsCreating(false);

    if (!result.success || !result.id) {
      setError(t('chat.errorCreate'));
      setStep('upload');
      return;
    }

    setCreatedJourneyId(result.id);
    setStep('done');
  }, [uploadedContent, uploadedFile, textInput, createJourney, t]);

  // ── First-time onboarding: skip journey creation, just mark complete ───

  const handleGetStarted = useCallback(async () => {
    await updateUser({ onboardingCompleted: true });
    setStep('done');
  }, [updateUser]);

  // ── Render ────────────────────────────────────────────────────────────────

  const timeframeOptions: Array<{ key: Timeframe; label: string }> = [
    { key: '1w', label: t('chat.timeframe1w') },
    { key: '2w', label: t('chat.timeframe2w') },
    { key: '1m', label: t('chat.timeframe1m') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-orange-50/30 to-indigo-50/20 flex flex-col items-center justify-center px-4 py-8">
      {/* Anna orb */}
      <div className="mb-6 flex flex-col items-center gap-2">
        <AnnaGlow size="lg" speaking={step === 'creating'} />
        <span className="text-sm text-gray-500 font-medium">Anna</span>
      </div>

      {/* Chat area */}
      <div className="w-full max-w-md flex flex-col gap-3">

        {/* Greeting bubble — only for additional mode (first-time uses welcome bubbles instead) */}
        {mode === 'additional' && (
          <ChatBubble sender="anna" visible={step !== 'greeting'}>
            <p className="text-sm leading-relaxed">
              {t('chat.greetingAdditional', { firstName })}
            </p>
          </ChatBubble>
        )}

        {/* Welcome bubbles — first-time onboarding only */}
        {mode === 'first' && step !== 'greeting' && (
          <>
            <ChatBubble sender="anna">
              <p className="text-sm leading-relaxed">
                {t('chat.welcomeBubble1', { firstName })}
              </p>
            </ChatBubble>
            <ChatBubble sender="anna">
              <p className="text-sm leading-relaxed">
                {t('chat.welcomeBubble2')}
              </p>
            </ChatBubble>
            <ChatBubble sender="anna">
              <p className="text-sm leading-relaxed">
                {t('chat.welcomeBubble3')}
              </p>
            </ChatBubble>
            {step === 'welcome' && (
              <button
                onClick={handleGetStarted}
                className="self-center mt-2 px-6 py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors shadow-sm"
              >
                {t('chat.welcomeButton')}
              </button>
            )}
          </>
        )}

        {/* Upload step — additional mode only */}
        {mode === 'additional' && (step === 'upload' || step === 'timeframe' || step === 'creating' || step === 'done') && (
          <>
            <ChatBubble sender="anna">
              <p className="text-sm leading-relaxed">
                {mode === 'additional'
                  ? t('chat.uploadPromptAdditional')
                  : t('chat.uploadPrompt')}
              </p>
            </ChatBubble>

            {/* Show uploaded file confirmation, or the input form */}
            {step === 'upload' && (
              <div className="flex flex-col gap-3">
                {/* Text input */}
                <ChatBubble sender="user" className="w-full max-w-full">
                  <div className="flex flex-col gap-2 w-full">
                    <textarea
                      className="w-full text-sm text-gray-700 bg-transparent border-0 outline-none resize-none placeholder-gray-400 min-h-[72px]"
                      placeholder={t('chat.uploadPlaceholder')}
                      value={textInput}
                      onChange={(e) => setTextInput(e.target.value)}
                      rows={3}
                    />
                  </div>
                </ChatBubble>

                {/* Drag-and-drop file zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`rounded-xl border-2 border-dashed px-4 py-4 text-center transition-colors cursor-pointer ${
                    isDragging
                      ? 'border-orange-300 bg-orange-50/60'
                      : 'border-gray-200 bg-white/40 hover:border-orange-200 hover:bg-orange-50/30'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                  aria-label={t('chat.uploadButton')}
                >
                  {uploadedFile ? (
                    <p className="text-sm text-orange-600 font-medium">
                      {t('chat.fileUploaded', { filename: uploadedFile.name })}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400">
                      <span className="text-orange-500 font-medium">{t('chat.uploadButton')}</span>
                      {' '}
                      {t('chat.dragDropHint')}
                    </p>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,.pdf"
                  className="hidden"
                  onChange={handleFileInputChange}
                  aria-label={t('chat.uploadButton')}
                />

                {/* Error message */}
                {error && (
                  <ChatBubble sender="system">
                    <span className="text-red-500 text-xs">{error}</span>
                  </ChatBubble>
                )}

                {/* Submit button */}
                <button
                  onClick={handleUploadSubmit}
                  className="self-end mt-1 px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors shadow-sm"
                >
                  {t('chat.submitButton')}
                </button>
              </div>
            )}

            {/* User confirms what they submitted */}
            {step !== 'upload' && (uploadedFile || textInput.trim()) && (
              <ChatBubble sender="user">
                <p className="text-sm">
                  {uploadedFile
                    ? t('chat.fileUploaded', { filename: uploadedFile.name })
                    : textInput.trim().slice(0, 80) + (textInput.trim().length > 80 ? '…' : '')}
                </p>
              </ChatBubble>
            )}
          </>
        )}

        {/* Timeframe step — additional mode only */}
        {mode === 'additional' && (step === 'timeframe' || step === 'creating' || step === 'done') && (
          <>
            <ChatBubble sender="anna">
              <p className="text-sm leading-relaxed">{t('chat.timeframePrompt')}</p>
            </ChatBubble>

            {step === 'timeframe' && (
              <div className="flex gap-2 justify-end">
                {timeframeOptions.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleTimeframeSelect(opt.key)}
                    className="px-4 py-2 rounded-full bg-white/80 border border-orange-200 text-gray-700 text-sm font-medium hover:bg-orange-50 hover:border-orange-400 transition-colors shadow-sm"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Creating step — additional mode only */}
        {mode === 'additional' && (step === 'creating' || step === 'done') && (
          <ChatBubble sender="anna">
            <p className="text-sm leading-relaxed">{t('chat.creatingPlan')}</p>
          </ChatBubble>
        )}

        {/* Done step */}
        {step === 'done' && (
          <ChatBubble sender="anna">
            <p className="text-sm leading-relaxed">
              {mode === 'additional'
                ? t('chat.doneMessageAdditional')
                : t('chat.doneMessage')}
            </p>
          </ChatBubble>
        )}

        {/* Error shown at creating step */}
        {error && step !== 'upload' && (
          <ChatBubble sender="system">
            <span className="text-red-500 text-xs">{error}</span>
          </ChatBubble>
        )}

        {/* Loading indicator during journey creation */}
        {isCreating && (
          <div className="flex justify-center py-2">
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full bg-orange-400"
                  style={{
                    animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Bounce keyframes for loading dots */}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default OnboardingChat;
